import { useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { PostureCounter, PostureStats } from '@bloo-team/api-client'
import { formatCompactCount } from '@bloo-team/api-client'
import { TimelineChart, shortTime, type TimeBucket } from '@bloo-team/core-shell'
import { Icon, RingGauge } from '@bloo-team/ui'
import { X } from '@bloo-team/ui/icons'
import { TopList } from '../TopPanel'
import { deltaView, mttdLabel, pctLabel } from '../../lib/signal-queue'

/**
 * The Signals stats (docs/signals.md §Stats cards): three cards in the
 * Sources layout — an uppercase eyebrow, the number in mono with a short
 * descriptor beside it.
 *
 * SIGNALS (wide): a ring of unassigned / all (the queue counts), the
 *   window's `cases/stats` signal count with its change, and the brushable
 *   volume chart.
 * ENTITIES: suspects · targets · detections with their change. The card is
 *   a button: it opens ONE panel under the cards with the three top-5 lists
 *   side by side.
 * RESPONSE: cases and mean time to detect.
 */

export type EntityList = 'detections' | 'suspects' | 'targets'

const ENTITY_LISTS: readonly EntityList[] = ['detections', 'suspects', 'targets']

export interface StatsVolume {
  buckets: TimeBucket[]
  sliceMs: number
  /** The dragged range narrowing the list; null = none. */
  range: { start: number; end: number } | null
  onSelectRange: (start: number, end: number) => void
  onClearRange: () => void
  /** The timeslice query's own state — the chart does not wait on, or fail with, the stats. */
  loading?: boolean
  failed?: boolean
}

/** The queue counts for the window (signals list, unfiltered); undefined while loading. */
export interface StatsQueue {
  unassigned: number | undefined
  all: number | undefined
}

function Delta({ counter, goodUp, window }: { counter: PostureCounter | undefined; goodUp: boolean; window: string }) {
  const d = deltaView(counter)
  const vs = `vs previous ${window}`
  if (d.kind === 'none') return <span className="sg-delta">–</span>
  if (d.kind === 'new') {
    return (
      <span className="sg-delta" title={`new — nothing in the previous ${window}`}>
        new
      </span>
    )
  }
  const mark = d.dir === 'up' ? '▲' : d.dir === 'down' ? '▼' : '–'
  const good = d.dir === 'flat' ? true : d.dir === 'up' ? goodUp : !goodUp
  return (
    <span className="sg-delta hud-delta" data-dir={d.dir} data-good={String(good)} title={vs} aria-label={`${mark === '▲' ? 'up' : mark === '▼' ? 'down' : 'flat'} ${pctLabel(d.pct)} ${vs}`}>
      {mark} {pctLabel(d.pct)}
    </span>
  )
}

/** One figure of a multi-figure card: value, unit, change. */
function Figure({ value, unit, delta }: { value: string; unit: string; delta: ReactNode }) {
  return (
    <span className="sg-figure">
      <span className="hud-value sg-figure-value">{value}</span>
      <span className="hud-unit">{unit}</span>
      {delta}
    </span>
  )
}

export function StatsBand({
  posture,
  loading,
  failed,
  window,
  volume,
  queue,
}: {
  posture: PostureStats | undefined
  loading: boolean
  failed: boolean
  window: string
  volume: StatsVolume
  queue: StatsQueue
}) {
  const uid = useId()
  const [open, setOpen] = useState(false)
  const card = useRef<HTMLButtonElement>(null)
  const n = (c: PostureCounter | undefined) => (loading ? '…' : c ? formatCompactCount(c.count) : '—')
  const mttd = posture?.mttdSeconds ?? null
  const detailId = `${uid}-detail`
  const { unassigned, all } = queue
  const ratio = unassigned !== undefined && all ? unassigned / all : 0

  const close = () => {
    setOpen(false)
    card.current?.focus()
  }

  const chart = volume.buckets.length ? (
    <TimelineChart buckets={volume.buckets} sliceMs={volume.sliceMs} style="bars" onSelectRange={volume.onSelectRange} />
  ) : (
    <p className="hud-unit" role="status">
      {volume.loading ? 'Loading…' : volume.failed ? 'Signal volume is unavailable.' : 'No signal volume in this window.'}
    </p>
  )

  const tops: Record<EntityList, { rows: PostureStats['topDetections'] | undefined; empty: string }> = {
    detections: { rows: posture?.topDetections, empty: 'No detections fired in this window.' },
    suspects: { rows: posture?.topSuspects, empty: 'No suspect entities in this window.' },
    targets: { rows: posture?.topTargets, empty: 'No target entities in this window.' },
  }

  return (
    <section className="sg-stats" aria-label="Signal statistics">
      <div className="hud-grid sg-cards">
        <div className="hud-panel hud-span-6 sg-card">
          <div className="sg-card-signals">
            <div className="hud-stack">
              <span className="hud-eyebrow">signals · {window}</span>
              <div className="hud-row sg-card-ring">
                <RingGauge value={ratio} size={64}>
                  <span className="hud-value sg-ring-n" aria-label={`${unassigned ?? '…'} unassigned of ${all ?? '…'}`}>
                    {unassigned === undefined ? '…' : formatCompactCount(unassigned)}/{all === undefined ? '…' : formatCompactCount(all)}
                  </span>
                </RingGauge>
                <div className="hud-stack sg-card-lead">
                  <span className="sg-figure">
                    <span className="hud-value">{n(posture?.signals)}</span>
                    <Delta counter={posture?.signals} goodUp={false} window={window} />
                  </span>
                  <span className="hud-unit">signals in this window</span>
                  <span className="hud-unit">ring: unassigned of all</span>
                </div>
              </div>
            </div>
            <div className="sg-volume">
              <span className="hud-eyebrow">volume</span>
              {chart}
              {volume.range ? (
                <span className="sg-chip sg-volume-range">
                  {shortTime(volume.range.start)} → {shortTime(volume.range.end)}
                  <button type="button" className="sg-chip-x" aria-label="Clear time filter" onClick={volume.onClearRange}>
                    ×
                  </button>
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <button
          ref={card}
          type="button"
          className="hud-panel hud-span-3 sg-card sg-card-btn"
          aria-expanded={open}
          aria-controls={open ? detailId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="hud-stack">
            <span className="hud-eyebrow">entities · top 5 ▾</span>
            <Figure value={n(posture?.suspects)} unit="suspects" delta={<Delta counter={posture?.suspects} goodUp={false} window={window} />} />
            <Figure value={n(posture?.targets)} unit="targets" delta={<Delta counter={posture?.targets} goodUp={false} window={window} />} />
            <Figure value={n(posture?.detections)} unit="detections" delta={<Delta counter={posture?.detections} goodUp={false} window={window} />} />
          </span>
        </button>

        <div className="hud-panel hud-span-3 sg-card">
          <div className="hud-stack">
            <span className="hud-eyebrow">response</span>
            <Figure value={n(posture?.cases)} unit="cases" delta={<Delta counter={posture?.cases} goodUp={false} window={window} />} />
            <Figure
              value={loading ? '…' : mttd === null ? '—' : mttdLabel(mttd)}
              unit="MTTD"
              delta={
                <span className="sg-delta" title="The server sends no previous-window MTTD to compare with.">
                  –
                </span>
              }
            />
            <span className="hud-unit sg-card-note">mean time to detect</span>
          </div>
        </div>
      </div>

      {open ? (
        <div id={detailId} className="sg-stat-detail" role="region" aria-label={`entities · ${window}`}>
          <div className="hud-row hud-row-between">
            <span className="hud-eyebrow">top 5 · {window} · scope-wide</span>
            <button type="button" className="qbc-x" aria-label="Close detail" title="Close" onClick={close}>
              <Icon icon={X} size={14} />
            </button>
          </div>
          <div className="sg-top-cols">
            {ENTITY_LISTS.map((t) => (
              <section key={t} className="hud-stack sg-top-col" aria-label={`top ${t}`}>
                <span className="hud-eyebrow">{t}</span>
                <TopList
                  rows={tops[t].rows ?? []}
                  limit={5}
                  compact
                  loading={loading}
                  failed={failed}
                  emptyText={tops[t].empty}
                  label={`top ${t}`}
                />
              </section>
            ))}
          </div>
        </div>
      ) : null}

      {failed ? (
        <p className="q-facet-note sg-stats-note" role="status">
          Server statistics are unavailable.
        </p>
      ) : null}
    </section>
  )
}
